class PurchaseOptionsComponent extends HTMLElement {
  constructor() {
    super();
    this.subscriptionPrice = this.getAttribute('data-subscription-price');
    this.originalPrice = this.getAttribute('data-original-price');
    this.currency = this.getAttribute('data-currency');
    this.productId = this.getAttribute('data-product-id');
    this.variantId = this.getAttribute('data-variant-id');
    this.discountPercentage = this.getAttribute('data-discount-percentage') || '10';
    this.sellingPlans = JSON.parse(this.getAttribute('data-selling-plans') || '[]');
    this.productFormId = this.getAttribute('data-product-form-id');
    this.currentVariant = null;
    
    // Initialize current variant from the page data
    this.initializeCurrentVariant();
  }

  initializeCurrentVariant() {
    
    // Try to get variant from various sources
    const variantSelector = document.querySelector('variant-selector');
    if (variantSelector) {
      const variants = JSON.parse(variantSelector.getAttribute('data-variants') || '[]');
      const currentVariantId = variantSelector.getAttribute('data-current-variant-id');
      this.currentVariant = variants.find(v => v.id == currentVariantId) || variants[0];
    }
    
    // If still no variant, try to get from window.meta or other sources
    if (!this.currentVariant && window.meta && window.meta.product && window.meta.product.selected_or_first_available_variant) {
      this.currentVariant = window.meta.product.selected_or_first_available_variant;
    }

    // Set defaults based on current variant's selling plans availability
    const hasSellingPlans = this.currentVariant && 
                           this.currentVariant.selling_plan_allocations && 
                           this.currentVariant.selling_plan_allocations.length > 0;


    if (hasSellingPlans && this.sellingPlans && this.sellingPlans.length > 0 && this.sellingPlans[0].selling_plans && this.sellingPlans[0].selling_plans.length > 0) {
      this.selectedOption = 'subscription';
      this.isCollapsed = false;
      this.sellingPlanId = this.sellingPlans[0].selling_plans[0].id;
      this.selectedFrequency = this.sellingPlans[0].selling_plans[0].name.toLowerCase();
    } else {
      this.selectedOption = 'onetime';
      this.isCollapsed = true;
      this.sellingPlanId = null;
      this.selectedFrequency = null;
    }
  }

  connectedCallback() {
    // Listen for variant changes first
    this.variantChangeListener = (event) => {
      this.handleVariantChange(event.detail);
    };
    document.addEventListener('variant:change', this.variantChangeListener);
    
    // Initialize with a slight delay to ensure other components are ready
    setTimeout(() => {
      // Re-check current variant in case it wasn't available in constructor
      if (!this.currentVariant) {
        this.initializeCurrentVariant();
      }
      
      this.render();
      this.attachEventListeners();
      this.updateFormInputs();
      // Update price display on initial load
      this.updatePriceDisplay();
    }, 10);
  }

  disconnectedCallback() {
    // Clean up event listener
    if (this.variantChangeListener) {
      document.removeEventListener('variant:change', this.variantChangeListener);
    }
  }

  handleVariantChange(variantData) {
    if (!variantData.variant) {
      console.warn('PurchaseOptions: No variant in change event');
      return;
    }
    
    
    this.currentVariant = variantData.variant;
    this.variantId = variantData.variant.id;
    

    document.querySelectorAll('.product-image img').forEach(img => {
      img.src = this.currentVariant.featured_image.src;
    });
    
    // Update prices
    this.originalPrice = (variantData.variant.price / 100).toFixed(2);
    this.subscriptionPrice = ((variantData.variant.price * 0.9) / 100).toFixed(2);
    
    // Update attributes
    this.setAttribute('data-variant-id', this.variantId);
    this.setAttribute('data-original-price', this.originalPrice);
    this.setAttribute('data-subscription-price', this.subscriptionPrice);
    
    // Check if variant has selling plans
    const hasSellingPlans = variantData.variant.selling_plan_allocations && 
                           variantData.variant.selling_plan_allocations.length > 0;
    
    
    if (!hasSellingPlans) {
      // Force one-time purchase if no subscription available
      this.selectedOption = 'onetime';
      this.isCollapsed = true;
      this.sellingPlanId = null;
      this.selectedFrequency = null;
    } else if (this.selectedOption === 'onetime' && hasSellingPlans) {
      // Re-enable subscription if it becomes available
      if (this.sellingPlans && this.sellingPlans.length > 0 && this.sellingPlans[0].selling_plans && this.sellingPlans[0].selling_plans.length > 0) {
        this.sellingPlanId = this.sellingPlans[0].selling_plans[0].id;
        this.selectedFrequency = this.sellingPlans[0].selling_plans[0].name.toLowerCase();
      }
    }
    
    // Re-render with updated data
    this.render();
    this.attachEventListeners();
    this.updateFormInputs();
    // Update price display when variant changes
    this.updatePriceDisplay();
  }

  renderSellingPlans() {
    if (!this.sellingPlans || this.sellingPlans.length === 0) {
      return '<button class="selling-plan py-[11px] lg:py-[13.3px] px-6 rounded-[100px] body3 capitalize font-bold text-center leading-[1.3] lg:leading-[1.1] hover:no-underline focus:no-underline selected bg-burgundy text-white">monthly</button>';
    }

    return this.sellingPlans.map(group => {
      return group.selling_plans.map(plan => {
        const planFrequency = plan.name.toLowerCase();
        const isSelected = this.selectedFrequency === planFrequency;
        const classes = `selling-plan py-[11px] lg:py-[13.3px] px-6 rounded-[100px] body3 capitalize font-bold text-center leading-[1.3] lg:leading-[1.1] hover:no-underline focus:no-underline ${isSelected ? 'selected bg-burgundy text-white' : 'bg-white text-burgundy border border-burgundy'}`;
        
        return `<button class="${classes}" data-frequency="${planFrequency}" data-plan-id="${plan.id}">${planFrequency}</button>`;
      }).join('');
    }).join('');
  }

  calculateDailyPrice() {
    const monthlyPrice = parseFloat(this.subscriptionPrice);
    const dailyPrice = (monthlyPrice / 30).toFixed(2);
    return `${this.currency}&nbsp;${dailyPrice}`;
  }

  findProductForm() {
    // Try to find form by specific ID first
    if (this.productFormId) {
      const formById = document.getElementById(this.productFormId);
      if (formById) return formById;
    }
    
    // Fallback to generic selectors
    return document.querySelector('product-form form') || 
           document.querySelector('form[data-type="add-to-cart-form"]') ||
           document.querySelector('.product-form form') ||
           document.querySelector('form[id*="product-form"]');
  }

  updateFormInputs() {
    const form = this.findProductForm();
    if (!form) {
      console.warn('Product form not found');
      return;
    }

    // Update or create selling plan input
    let sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      form.appendChild(sellingPlanInput);
    }

    // Set selling plan value based on selection
    if (this.selectedOption === 'subscription' && this.sellingPlanId) {
      sellingPlanInput.value = this.sellingPlanId;
    } else {
      sellingPlanInput.value = '';
    }

    // Update price display in the buy button
    this.updatePriceDisplay();
    
    // Dispatch event to notify form about the change
    form.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updatePriceDisplay() {
    // Find the price display in the buy button
    const priceDisplays = document.querySelectorAll(
      '[data-price-display], ' +
      '.product-add-to-cart-form .selling-plan-price div'
    );
    
    const displayPrice = this.selectedOption === 'subscription' ? this.subscriptionPrice : this.originalPrice;
    
    
    priceDisplays.forEach(priceDisplay => {
      if (priceDisplay) {
        // Format price with currency symbol (simple format to match Shopify)
        priceDisplay.textContent = `${this.currency || '$'}${displayPrice}`;
      }
    });
  }

  render() {
    // Check if current variant has selling plans available
    const hasSellingPlans = this.currentVariant && 
                           this.currentVariant.selling_plan_allocations && 
                           this.currentVariant.selling_plan_allocations.length > 0;
    
    this.innerHTML = `
      <div class="purchase-options-wrapper">
        <!-- Subscription Option -->
        <div class="relative bg-white rounded-2xl border-[1.5px] py-6 px-4 lg:p-6 min-h-[88px] flex flex-col justify-center mb-4 ${this.selectedOption === 'subscription' && hasSellingPlans ? 'border-orange' : 'border-transparent'} ${!hasSellingPlans ? 'opacity-50' : ''}">
          <button type="button" class="subscription-toggle flex items-center w-full body2 leading-[1.1] lg:leading-[1.3] font-bold rtl:text-right ${!hasSellingPlans ? 'cursor-not-allowed' : ''}" 
                  aria-expanded="${!this.isCollapsed}" aria-controls="subscription-content" ${!hasSellingPlans ? 'disabled' : ''}>
            <span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="0.75" y="0.75" width="22.5" height="22.5" rx="11.25" stroke="${!hasSellingPlans ? '#ccc' : '#FF6B3D'}" stroke-width="1.5"></rect>
                ${this.selectedOption === 'subscription' && hasSellingPlans ? '<rect x="4" y="4" width="16" height="16" rx="8" fill="#FF6B3D"></rect>' : ''}
              </svg>
            </span>
            <span class="ml-3 flex gap-3 items-center">Subscribe and save ${!hasSellingPlans ? '(Not available for this variant)' : ''}
              ${hasSellingPlans ? `<span class="transition-all ${this.isCollapsed ? 'rotate-0' : 'rotate-180'}">
                <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 256 256" class="text-burgundy w-[18px] h-[18px]" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                  <title>Drop down</title>
                  <path d="M216.49,104.49l-80,80a12,12,0,0,1-17,0l-80-80a12,12,0,0,1,17-17L128,159l71.51-71.52a12,12,0,0,1,17,17Z"></path>
                </svg>
              </span>` : ''}
            </span>
            ${hasSellingPlans ? `<span class="flex gap-6 ml-auto items-center">
              <p class="absolute lg:static -top-4 right-4 body3 font-normal px-3 py-2 bg-yellow rounded-[100px]">Save ${this.discountPercentage}%</p>
              <div class="selling-plan-price flex gap-2">
                <s class="font-normal text-burgundy/30">
                  <div>${this.currency}&nbsp;${this.originalPrice}</div>
                </s>
                <div>${this.currency}&nbsp;${this.subscriptionPrice}</div>
              </div>
            </span>` : ''}
          </button>
          
          <div id="subscription-content" class="subscription-content overflow-hidden border-t border-burgundy-20 transition-all duration-300 ease-in-out" 
               style="height: ${this.isCollapsed ? '0' : 'auto'}; opacity: ${this.isCollapsed ? '0' : '1'}; margin-top: ${this.isCollapsed ? '0' : '20px'};">
            <div>
              <div class="mb-6 lg:mb-8 flex flex-col md:flex-row gap-5">
                <div class="flex flex-col gap-3 mt-6">
                  <div class="flex gap-2 items-center">
                    <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 256 256" class="w-6 h-6" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                      <path d="M172.24,99.76a6,6,0,0,1,0,8.48l-56,56a6,6,0,0,1-8.48,0l-24-24a6,6,0,0,1,8.48-8.48L112,151.51l51.76-51.75A6,6,0,0,1,172.24,99.76ZM230,128A102,102,0,1,1,128,26,102.12,102.12,0,0,1,230,128Zm-12,0a90,90,0,1,0-90,90A90.1,90.1,0,0,0,218,128Z"></path>
                    </svg>
                    <p class="body3">10% off future deliveries.</p>
                  </div>
                  <div class="flex gap-2 items-center">
                    <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 256 256" class="w-6 h-6" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                      <path d="M172.24,99.76a6,6,0,0,1,0,8.48l-56,56a6,6,0,0,1-8.48,0l-24-24a6,6,0,0,1,8.48-8.48L112,151.51l51.76-51.75A6,6,0,0,1,172.24,99.76ZM230,128A102,102,0,1,1,128,26,102.12,102.12,0,0,1,230,128Zm-12,0a90,90,0,1,0-90,90A90.1,90.1,0,0,0,218,128Z"></path>
                    </svg>
                    <p class="body3">Free shipping on all orders.</p>
                  </div>
                  <div class="flex gap-2 items-center">
                    <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 256 256" class="w-6 h-6" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                      <path d="M172.24,99.76a6,6,0,0,1,0,8.48l-56,56a6,6,0,0,1-8.48,0l-24-24a6,6,0,0,1,8.48-8.48L112,151.51l51.76-51.75A6,6,0,0,1,172.24,99.76ZM230,128A102,102,0,1,1,128,26,102.12,102.12,0,0,1,230,128Zm-12,0a90,90,0,1,0-90,90A90.1,90.1,0,0,0,218,128Z"></path>
                    </svg>
                    <p class="body3">Cancel subscription anytime.</p>
                  </div>
                </div>
                <div class="md:mt-auto md:ml-auto md:pr-1">
                  <p class="body3 font-semibold uppercase leading-[1.2]">
                    <i>Less than <span class="inline-block">${this.calculateDailyPrice()}</span> per day! </i>
                  </p>
                </div>
              </div>
              <div class="flex items-center justify-between gap-2 mb-4 lg:mb-5">
                <p class="body2 font-bold">Deliver for:</p>
              </div>
              <div class="grid grid-cols-2 lg:flex gap-2">
                ${this.renderSellingPlans()}
              </div>
            </div>
          </div>
        </div>

        <!-- One-time Purchase Option -->
        <button class="one-time-purchase min-h-[88px] flex w-full gap-3 bg-white items-center rounded-2xl border-[1.5px] p-6 mb-4 body2 leading-[1.3] font-bold transition-all duration-200 ${this.selectedOption === 'onetime' ? 'border-orange' : 'border-transparent'}">
          <div class="mr-auto flex gap-3 items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="0.75" y="0.75" width="22.5" height="22.5" rx="11.25" stroke="#FF6B3D" stroke-width="1.5"></rect>
              ${this.selectedOption === 'onetime' ? '<rect x="4" y="4" width="16" height="16" rx="8" fill="#FF6B3D"></rect>' : ''}
            </svg>One-time purchase
          </div>
          <div class="product-price">
            <div>${this.currency}&nbsp;${this.originalPrice}</div>
          </div>
        </button>
      </div>
    `;
  }

  attachEventListeners() {
    const subscriptionToggle = this.querySelector('.subscription-toggle');
    const onetimePurchase = this.querySelector('.one-time-purchase');
    const sellingPlanButtons = this.querySelectorAll('.selling-plan');

    // Check if current variant has selling plans available
    const hasSellingPlans = this.currentVariant && 
                           this.currentVariant.selling_plan_allocations && 
                           this.currentVariant.selling_plan_allocations.length > 0;

    // Subscription toggle event
    subscriptionToggle.addEventListener('click', () => {
      // If no selling plans available, don't allow subscription selection
      if (!hasSellingPlans) {
        return;
      }

      if (this.selectedOption === 'subscription') {
        // Toggle collapse/expand
        this.isCollapsed = !this.isCollapsed;
      } else {
        // Select subscription and expand
        this.selectedOption = 'subscription';
        this.isCollapsed = false;
        // Restore first selling plan if none selected
        if (!this.sellingPlanId && this.sellingPlans && this.sellingPlans.length > 0 && this.sellingPlans[0].selling_plans && this.sellingPlans[0].selling_plans.length > 0) {
          this.sellingPlanId = this.sellingPlans[0].selling_plans[0].id;
          this.selectedFrequency = this.sellingPlans[0].selling_plans[0].name.toLowerCase();
        }
      }

      this.render();
      this.attachEventListeners();
      this.updateFormInputs();
      this.dispatchSelectionEvent();
    });

    // One-time purchase event
    onetimePurchase.addEventListener('click', () => {
      this.selectedOption = 'onetime';
      this.isCollapsed = true; // Collapse subscription when selecting one-time
      this.sellingPlanId = null; // Clear selling plan for one-time purchase

      this.render();
      this.attachEventListeners();
      this.updateFormInputs();
      this.dispatchSelectionEvent();
    });

    // Selling plan frequency selection
    sellingPlanButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectedFrequency = button.getAttribute('data-frequency');
        this.sellingPlanId = button.getAttribute('data-plan-id');

        this.render();
        this.attachEventListeners();
        this.updateFormInputs();
        this.dispatchSelectionEvent();
      });
    });
  }

  dispatchSelectionEvent() {
    // Update price display when selection changes
    this.updatePriceDisplay();
    
    // Dispatch custom event when selection changes
    const event = new CustomEvent('purchase-option-changed', {
      detail: {
        selectedOption: this.selectedOption,
        isCollapsed: this.isCollapsed,
        price: this.selectedOption === 'subscription' ? this.subscriptionPrice : this.originalPrice,
        sellingPlanId: this.selectedOption === 'subscription' ? this.sellingPlanId : null
      }
    });
    this.dispatchEvent(event);
  }
}

// Register the custom element
customElements.define('purchase-options-component', PurchaseOptionsComponent);

// Listen for purchase option changes globally (optional)
document.addEventListener('purchase-option-changed', (event) => {
  // You can add additional logic here if needed
});

class VariantSelector extends HTMLElement {
  constructor() {
    super();
    this.productId = this.getAttribute('data-product-id');
    this.productHandle = this.getAttribute('data-product-handle');
    this.currentVariantId = this.getAttribute('data-current-variant-id');
    this.variants = JSON.parse(this.getAttribute('data-variants') || '[]');
    this.selectedOptions = {};
    this.currentVariant = null;
    
    // Find current variant
    this.currentVariant = this.variants.find(variant => variant.id == this.currentVariantId) || this.variants[0];
    
    
    // Initialize selected options from current variant
    if (this.currentVariant && this.currentVariant.options) {
      this.currentVariant.options.forEach((option, index) => {
        this.selectedOptions[`option${index + 1}`] = option;
      });
    }
  }

  connectedCallback() {
    this.attachEventListeners();
    this.updateFormInputs();
    // Mark initial variant as selected
    this.updateDataVariantSelected(true);
    // Update initial option styles to match selected options
    this.updateOptionStyles();
    
    // Dispatch initial variant change event after a short delay to ensure other components are ready
    setTimeout(() => {
      this.dispatchVariantChangeEvent();
    }, 50);
  }

  attachEventListeners() {
    const radioInputs = this.querySelectorAll('.product-option-input');
    
    radioInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        if (e.target.checked) {
          const position = e.target.getAttribute('data-option-position');
          const value = e.target.getAttribute('data-option-value');
          
          // Update selected options
          this.selectedOptions[`option${position}`] = value;
          
          
          // Find matching variant
          this.findMatchingVariant();
          
          // Update UI
          this.updateOptionStyles();
          this.updatePurchaseOptions();
          this.updateFormInputs();
          
          // Dispatch events
          this.dispatchVariantChangeEvent();
        }
      });
    });
  }

  findMatchingVariant() {
    // Find variant that matches selected options
    this.currentVariant = this.variants.find(variant => {
      return variant.options.every((option, index) => {
        return option === this.selectedOptions[`option${index + 1}`];
      });
    });

    if (!this.currentVariant) {
      // Fallback to first available variant
      this.currentVariant = this.variants[0];
    }

    // Update current variant ID
    this.currentVariantId = this.currentVariant.id;
    this.setAttribute('data-current-variant-id', this.currentVariantId);
  }

  updateOptionStyles() {
    const radioInputs = this.querySelectorAll('.product-option-input');
    
    radioInputs.forEach(input => {
      const span = input.nextElementSibling;
      const position = input.getAttribute('data-option-position');
      const value = input.getAttribute('data-option-value');
      
      // Check if this option should be selected based on our selectedOptions object
      const shouldBeSelected = this.selectedOptions[`option${position}`] === value;
      
      // Update the input's checked state to match our selectedOptions
      if (input.checked !== shouldBeSelected) {
        input.checked = shouldBeSelected;
      }
      
      // Check if this option value combination is available in any variant
      const isAvailable = this.isOptionValueAvailable(position, value);
      
      if (shouldBeSelected) {
        // Selected state
        span.className = span.className.replace(/bg-gray-100|text-burgundy|hover:bg-gray-200|opacity-50|cursor-not-allowed/g, '');
        if (isAvailable) {
          span.className += ' bg-burgundy text-white';
        } else {
          span.className += ' bg-burgundy text-white opacity-50';
        }
      } else {
        // Unselected state
        span.className = span.className.replace(/bg-burgundy|text-white/g, '');
        if (isAvailable) {
          span.className += ' bg-gray-100 text-burgundy hover:bg-gray-200';
          input.disabled = false;
        } else {
          span.className += ' bg-gray-100 text-burgundy opacity-50 cursor-not-allowed';
          input.disabled = true;
        }
      }
    });
    
    // Update add to cart buttons based on current variant availability
    this.updateAddToCartButtons();
  }

  isOptionValueAvailable(position, value) {
    // Check if this option value is available in any variant
    return this.variants.some(variant => {
      const optionIndex = parseInt(position) - 1;
      return variant.options[optionIndex] === value && variant.available;
    });
  }

  updateAddToCartButtons() {
    // Look for add to cart buttons in the product form area
    const addToCartButtons = document.querySelectorAll(
      '.product-add-to-cart-form button[type="submit"], ' +
      'product-form button[type="submit"], ' +
      'button[name="add"], ' +
      'button[data-type="add-to-cart"], ' +
      '.product-form button[type="submit"]'
    );
    const isAvailable = this.currentVariant && this.currentVariant.available;
    
    addToCartButtons.forEach(button => {
      if (isAvailable) {
        button.disabled = false;
        button.classList.remove('opacity-50', 'cursor-not-allowed');
        // Don't change button text/content to preserve styling and icons
      } else {
        button.disabled = true;
        button.classList.add('opacity-50', 'cursor-not-allowed');
        // Don't change button text/content to preserve styling and icons
      }
    });
  }

  updatePurchaseOptions() {
    // Find purchase options component
    const purchaseOptionsComponent = document.querySelector('purchase-options-component');
    
    if (purchaseOptionsComponent && this.currentVariant) {
      // Update variant ID
      purchaseOptionsComponent.variantId = this.currentVariant.id;
      purchaseOptionsComponent.setAttribute('data-variant-id', this.currentVariant.id);
      
      // Update prices
      const originalPrice = (this.currentVariant.price / 100).toFixed(2);
      const subscriptionPrice = ((this.currentVariant.price * 0.9) / 100).toFixed(2);
      
      purchaseOptionsComponent.originalPrice = originalPrice;
      purchaseOptionsComponent.subscriptionPrice = subscriptionPrice;
      purchaseOptionsComponent.setAttribute('data-original-price', originalPrice);
      purchaseOptionsComponent.setAttribute('data-subscription-price', subscriptionPrice);
      
      // Check if variant has selling plans (subscription available)
      const hasSellingPlans = this.currentVariant.selling_plan_allocations && 
                             this.currentVariant.selling_plan_allocations.length > 0;
      
      if (!hasSellingPlans) {
        // Force one-time purchase if no subscription available
        purchaseOptionsComponent.selectedOption = 'onetime';
        purchaseOptionsComponent.isCollapsed = true;
        purchaseOptionsComponent.sellingPlanId = null;
      }
      
      // Re-render purchase options
      purchaseOptionsComponent.render();
      purchaseOptionsComponent.attachEventListeners();
      purchaseOptionsComponent.updateFormInputs();
    }

    // Update product information display
    this.updateProductInfo();
  }

  updateProductInfo() {
    if (!this.currentVariant) return;

    // Update price displays
    const priceElements = document.querySelectorAll('[data-price]');
    priceElements.forEach(element => {
      const price = (this.currentVariant.price / 100).toFixed(2);
      element.textContent = `$${price}`;
    });

    // Update variant title/SKU if exists
    const variantTitleElements = document.querySelectorAll('[data-variant-title]');
    variantTitleElements.forEach(element => {
      if (this.currentVariant.title && this.currentVariant.title !== 'Default Title') {
        element.textContent = this.currentVariant.title;
      }
    });

    // Update inventory status
    const inventoryElements = document.querySelectorAll('[data-inventory-status]');
    inventoryElements.forEach(element => {
      if (this.currentVariant.available) {
        if (this.currentVariant.inventory_quantity > 0) {
          element.textContent = `${this.currentVariant.inventory_quantity} in stock`;
          element.className = element.className.replace(/text-red-500|text-yellow-500/g, '') + ' text-green-500';
        } else {
          element.textContent = 'Available';
          element.className = element.className.replace(/text-red-500|text-yellow-500/g, '') + ' text-green-500';
        }
      } else {
        element.textContent = 'Out of stock';
        element.className = element.className.replace(/text-green-500|text-yellow-500/g, '') + ' text-red-500';
      }
    });

    // Update product URL for sharing/bookmarking
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url);
    }
  }

  updateFormInputs() {
    // Update hidden variant ID input in product form - try multiple selectors
    const productForms = document.querySelectorAll(
      'form[data-type="add-to-cart-form"], ' +
      'form[action*="/cart/add"], ' +
      'product-form form, ' +
      'form:has(input[name="id"]), ' +
      '.product-form form, ' +
      '.product-add-to-cart-form form'
    );
    
    productForms.forEach(form => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && this.currentVariant) {
        variantInput.value = this.currentVariant.id;
        
        // Trigger change event to notify other components
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Also update any variant-related data attributes
    const productContainers = document.querySelectorAll('[data-product-id]');
    productContainers.forEach(container => {
      if (this.currentVariant) {
        container.setAttribute('data-variant-id', this.currentVariant.id);
        container.setAttribute('data-variant-available', this.currentVariant.available);
        container.setAttribute('data-variant-price', this.currentVariant.price);
      }
    });
  }

  updateDataVariantSelected(isSelected) {
    // Update data-variant-selected attribute on product parent elements
    const productParents = document.querySelectorAll('[data-section][data-product]');
    productParents.forEach(parent => {
      parent.setAttribute('data-variant-selected', isSelected.toString());
    });
  }

  dispatchVariantChangeEvent() {
    // Check if variant actually changed to avoid unnecessary events
    if (this.currentVariant && this.lastDispatchedVariantId === this.currentVariant.id) {
      return;
    }

    this.lastDispatchedVariantId = this.currentVariant ? this.currentVariant.id : null;

    // Dispatch variant change event for other components to listen
    const event = new CustomEvent('variant:change', {
      detail: {
        variant: this.currentVariant,
        selectedOptions: this.selectedOptions,
        available: this.currentVariant ? this.currentVariant.available : false,
        price: this.currentVariant ? this.currentVariant.price : 0,
        productId: this.productId
      }
    });
    
    document.dispatchEvent(event);
    
    // Also dispatch the pubsub event
    if (typeof publish === 'function') {
      publish('variant:change', {
        detail: {
          variant: this.currentVariant,
          selectedOptions: this.selectedOptions,
          available: this.currentVariant ? this.currentVariant.available : false,
          price: this.currentVariant ? this.currentVariant.price : 0,
          productId: this.productId
        }
      });
    }

    // Check for cart conflicts and handle them
    this.handleCartConflicts();
  }

  async handleCartConflicts() {
    // Only handle conflicts if we have a current variant
    if (!this.currentVariant) {
      return;
    }

    // Try to get cart data
    let cartData;
    try {
      // Try different ways to get cart data
      if (typeof getCart === 'function') {
        cartData = await getCart();
      } else {
        // Fallback to fetch cart directly
        const response = await fetch('/cart.js');
        cartData = await response.json();
      }
    } catch (error) {
      console.warn('Could not fetch cart data:', error);
      return;
    }

    if (!cartData || !cartData.items) {
      return;
    }

    // Find any items from the same product with different variants
    const conflictingItems = cartData.items.filter(item => 
      item.product_id == this.productId && 
      item.variant_id != this.currentVariant.id
    );

    if (conflictingItems.length > 0) {
      // Optionally notify user or auto-handle conflicts
      // For now, just log for debugging
      conflictingItems.forEach(item => {
      });
    }
  }
}

// Register the custom element
customElements.define('variant-selector', VariantSelector);


// Comparison Table
class ComparisonTable extends HTMLElement {
  constructor() {
      super();
  }

  connectedCallback() {
      this.render();
  }

  static get observedAttributes() {
      return ['data'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'data' && oldValue !== newValue) {
          this.render();
      }
  }

  render() {
      const dataAttr = this.getAttribute('data');
      if (!dataAttr) {
          this.innerHTML = '<p>No data provided</p>';
          return;
      }

      try {
          const tableData = JSON.parse(dataAttr);
          this.innerHTML = this.createTable(tableData);
      } catch (error) {
          this.innerHTML = '<p>Invalid JSON data</p>';
          console.error('Comparison Table Error:', error);
      }
  }

  createTable(data) {
      if (!data.columns || !data.rows) {
          return '<p>Invalid data structure</p>';
      }

      const headerRow = data.columns.map(column => 
          `<th class="descriptor font-inter uppercase border-r border-burgundy-20 last:border-none p-0 pb-[6px] pr-4 lg:pr-6 last:pr-0 pl-4 lg:pl-6 first:pl-0">${column}</th>`
      ).join('');

      const bodyRows = data.rows.map(row => {
          const cells = data.columns.map(column => {
              const cellValue = row[column];
              let content = '';
              let style = '';

              if (typeof cellValue === 'object' && cellValue.value) {
                  content = cellValue.value;
                  if (cellValue.textColor) {
                      style = `style="color: ${cellValue.textColor};"`;
                  }
              } else {
                  content = cellValue;
              }

              const defaultStyle = !style ? 'style="color: rgb(56, 4, 35);"' : style;

              return `<td class="body3 border-r font-inter border-burgundy-20 last:border-none py-[6px] pr-4 lg:pr-6 last:pr-0 pl-4 lg:pl-6 first:pl-0 leading-[1.3] font-normal" ${defaultStyle}>${content}</td>`;
          }).join('');

          return `<tr>${cells}</tr>`;
      }).join('');

      return `
          <table class="w-fit table-auto text-left border-collapse">
              <thead>
                  <tr>${headerRow}</tr>
              </thead>
              <tbody>
                  ${bodyRows}
              </tbody>
          </table>
      `;
  }
}

// Backed by data percentage circle

class ResponsiveProgressSVG extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes() {
    return ["percentage", "product"];
  }

  connectedCallback() {
    this.render();
    window.addEventListener("resize", () => this.render());
  }

  attributeChangedCallback(oldValue, newValue) {
    if (oldValue !== newValue) this.render();
  }

  isMobile() {
    return window.innerWidth < 768; // md breakpoint
  }

  calculateDotPosition(centerX, centerY, radius, percentage) {
    const angle = (percentage / 100) * 360 - 90;
    const radians = (angle * Math.PI) / 180;
    return {
      x: (centerX + radius * Math.cos(radians)).toFixed(4),
      y: (centerY + radius * Math.sin(radians)).toFixed(4),
    };
  }

  getTextContent(productTitle, percentage) {
    const index = parseInt(this.getAttribute("index"));
    if (productTitle.includes("bones") && percentage === 0) return "+";
    if (productTitle.includes("tendons") && percentage === 0) return "↓";
    if (productTitle.includes("brain") && index === 2) return `${percentage}k%`;
    if (productTitle.includes("muscle") && index === 1) return `2x`;
    if (productTitle.includes("muscle") && index === 2) return `+`;
    if (productTitle.includes("muscle") && index === 3) {
      return `
        <tspan x="50%" dy="-0.4em">Up to</tspan>
        <tspan x="50%" dy="1.2em">${percentage}%</tspan>
      `;
    }
    return `${percentage}%`;
  }

  getConfig() {
    const isMobile = this.isMobile();
    const productTitle = (this.getAttribute("product") || "").toLowerCase();
    const index = parseInt(this.getAttribute("index") || "0", 10);
  
    const base = isMobile
      ? {
          width: 74,
          height: 74,
          center: 37,
          radius: 36.25,
          innerRadius: 32.25,
          strokeWidth: 1.5,
          dotRadius: 2.5,
          fontSize: 26,
          letterSpacing: "-0.52px",
        }
      : {
          width: 116,
          height: 116,
          center: 58,
          radius: 57,
          innerRadius: 50,
          strokeWidth: 2,
          dotRadius: 3,
          fontSize: 40,
          letterSpacing: "-0.8px",
        };
  
    if (productTitle.includes("muscle") && index === 3) {
      base.fontSize = isMobile ? 20 : 30;
    }
  
    return base;
  }

  createSVG(config) {
    const percentage = parseFloat(this.getAttribute("percentage")) || 0;
    const productTitle = this.getAttribute("product") || "";
    const { width, height, center, radius, innerRadius, strokeWidth, dotRadius, fontSize, letterSpacing } = config;

    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (percentage / 100) * circumference;
    const dot = this.calculateDotPosition(center, center, radius, percentage);

    const textContent = this.getTextContent(productTitle, percentage);

    return `
      <svg width="${width}" height="${height}" class="block overflow-visible" style="min-width:${width}px; min-height:${height}px;">
        <circle cx="${center}" cy="${center}" r="${radius}" stroke="#3804234d" stroke-width="${strokeWidth}" fill="none"></circle>
        
        <circle cx="${center}" cy="${center}" r="${radius}" 
                stroke="#380423" 
                stroke-width="${strokeWidth}" 
                fill="none" 
                stroke-dasharray="${circumference}" 
                stroke-dashoffset="${dashOffset}" 
                stroke-linecap="round" 
                style="transform: rotate(-90deg); transform-origin: 50% 50%;"></circle>
        
        <circle cx="${dot.x}" cy="${dot.y}" r="${dotRadius}" fill="#380423"></circle>
        <circle cx="${center}" cy="${center}" r="${innerRadius}" fill="#380423"></circle>
        
        ${textContent ? `<text x="50%" y="50%" text-anchor="middle" dominant-baseline="central" font-family="'Libre Caslon Condensed', serif" fill="#fff" font-size="${fontSize}" font-weight="400" letter-spacing="${letterSpacing}">
                ${textContent}
               </text>`
            : ""
        }
      </svg>
    `;
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
        }
      </style>
      ${this.createSVG(this.getConfig())}
    `;
  }
}

customElements.define("responsive-progress-svg", ResponsiveProgressSVG);



// Custom element'i register et
customElements.define('comparison-table', ComparisonTable);

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.toggle-sound').forEach(toggleSound => {
    const video = toggleSound.parentNode.querySelector('video');

    toggleSound.addEventListener('click', () => {
      video.muted = !video.muted;
      toggleSound.classList.toggle('mute', video.muted);
      toggleSound.classList.toggle('unmute', !video.muted);
    });
  });

  const imageBack = document.querySelectorAll(".media-container .product-media")[1]
  const defaultImageSrc = imageBack.querySelector('img').src

  document.addEventListener('variant:change', (event) => {
    const variantId = event?.detail?.variant?.id;
    let imageSrc = null
    if(window.backOfBoxImages && variantId && imageBack){
      window.backOfBoxImages.forEach(item => {
        item.variants.forEach(variant => {
          if(variant.indexOf(variantId) >= 0){
            imageSrc = item.image
          }
        });
      });

      if(imageSrc){
        const image = imageBack.querySelector('img')
        const source = imageBack.querySelector('source')
        image.src = imageSrc
        image.removeAttribute('srcset')
        image.removeAttribute('sizes')
        if(source){
          source.remove()
        }
      }else{
        const image = imageBack.querySelector('img')
        image.src = defaultImageSrc
      }
    }
  });
});


